// SPEC-COLLECTION-METER-CARRY-COMPENSATION-V1 (D-583) 集金の 2 点引き算に、期間中のメーター不連続を足し戻す。
//   ★ひろ指摘「集金の時も一周の引き継ぎをしておかないと集金金額の計算がずれるんじゃないの？」。
//   ★集金は 差 = 今回IN − 前回IN の 2 点引き算。期間の途中で交換/一周があると素の引き算はずれる。
//
// SPEC-METER-EXCHANGE-REPORT-01 (D-994): 足し戻す量は meterBoundary.js の exchangeCompensation(唯一の式から導いた形)。
//   ・一周: max + 1(例: 6 桁なら 1,000,000)
//   ・交換: 旧メーターの最終値 − 新しいメーターの区切りの値(new)
//   ★見るのは IN 本体(in_meter)の報告だけ。集金額は IN の増分 × 単価なので、OUT だけの交換/一周は 0。
//   ★報告はその日のイベント行(SQL fn_meter_exchange_rows と同じ規則)にあるものだけを数える
//     (同じ日に何回保存しても 1 回だけ足す = field failure 1 の型を作らない)。
//   ★最終値が分からない交換(旧データ)は補正できない。黙って一部だけ足さず「要確認」で返す。
//   ★D-1073: 下がったと報告(new < final)してイベント行の IN が下がっていない(in_meter >= final)交換も足さずに「要確認」。
import { exchangeCompensation, exchangeRowsByDay, pickExchange } from './meterBoundary'

/**
 * イベント 1 件ぶん(IN 本体)の足し戻し量。IN の報告が無ければ 0。補正不能なら null。
 * @param {{meter_exchanges?: Array|null, in_meter?: number|null}} row その日のイベント行
 */
export function compensationForEvent(row) {
  const ev = pickExchange(row?.meter_exchanges, 'in_meter')
  if (!ev) return 0
  // D-1073: イベント行の今回値(in_meter)も渡す。下がったと報告して下がっていない交換は足さない(null = 要確認)。
  return exchangeCompensation(ev, row?.in_meter)
}

/** D-1073 直し: 補正できなかった理由(画面の「要確認」の文言を出し分ける)。 */
export const CARRY_UNKNOWN_REASON = Object.freeze({ FINAL_UNKNOWN: 'final_unknown', NOT_DROPPED: 'not_dropped' })

/**
 * 補正できなかったイベント行の理由。今回値を渡さなければ計算できる(= 最終値は分かっている)なら
 * 「下がったと報告して下がっていない」、それ以外は「旧メーター最終値が不明」。
 */
function unknownReasonForEvent(row) {
  const ev = pickExchange(row?.meter_exchanges, 'in_meter')
  return ev && exchangeCompensation(ev) != null ? CARRY_UNKNOWN_REASON.NOT_DROPPED : CARRY_UNKNOWN_REASON.FINAL_UNKNOWN
}

/**
 * 集金画面の「要確認」に出す種類。理由を持たない行(meter_carry_unknown だけ)は今までどおり最終値不明。
 * @param {Array<{meter_carry_unknown?: boolean, meter_carry_unknown_reasons?: string[]}>} booths
 */
export function carryUnknownKinds(booths) {
  let finalUnknown = false
  let mismatch = false
  for (const b of booths ?? []) {
    if (!b?.meter_carry_unknown) continue
    const reasons = Array.isArray(b.meter_carry_unknown_reasons) ? b.meter_carry_unknown_reasons : []
    if (reasons.includes(CARRY_UNKNOWN_REASON.NOT_DROPPED)) mismatch = true
    if (reasons.length === 0 || reasons.includes(CARRY_UNKNOWN_REASON.FINAL_UNKNOWN)) finalUnknown = true
  }
  return { finalUnknown, mismatch }
}

/**
 * ブースごとの足し戻し量を合計する。
 * @param {Array<{booth_code: string, patrol_date: string, meter_exchanges: Array|null}>} rows meter_readings の行(複数ブース混在可)
 * @returns {{byBooth: Record<string, number>, unknownBooths: string[], unknownReasons: Record<string, string[]>}}
 */
export function sumCompensations(rows) {
  const byBooth = {}
  const unknown = new Set()
  const reasons = {}
  const perBooth = new Map()
  for (const r of rows ?? []) {
    if (!r?.booth_code) continue
    if (!perBooth.has(r.booth_code)) perBooth.set(r.booth_code, [])
    perBooth.get(r.booth_code).push(r)
  }
  for (const [code, list] of perBooth) {
    for (const evRow of exchangeRowsByDay(list).values()) {
      const c = compensationForEvent(evRow)
      if (c == null) {
        unknown.add(code)
        const r = unknownReasonForEvent(evRow)
        reasons[code] = reasons[code] ?? []
        if (!reasons[code].includes(r)) reasons[code].push(r)
        continue
      }
      if (c !== 0) byBooth[code] = (byBooth[code] ?? 0) + c
    }
  }
  return { byBooth, unknownBooths: [...unknown], unknownReasons: reasons }
}
